/**
 * Type Registry
 *
 * Maps API `__type` values to their formatter, Redux store key, ID extractor,
 * and flatten configuration. This drives the auto-normalizer: when a response
 * object carries `__type`, the registry tells us how to format it, where to
 * store it, and which nested entities to extract.
 */

import {
  formatSpace,
  formatSpaceMember,
  formatKit,
  formatKitMember,
  formatKitVersion,
  formatSection,
  formatItem,
  formatAsset,
  formatPortal,
  formatPortalItem,
  formatPortalMember,
  formatField,
  formatProduct,
  formatSpacePreview,
  formatVariant,
} from "./entityFormatters";
import {
  AnyObject,
  APIToken,
  Asset,
  AssetView,
  CustomField,
  DirectLink,
  DownloadRequest,
  Invitation,
  Item,
  Kit,
  KitMember,
  KitVersion,
  Market,
  Portal,
  PortalItem,
  PortalMember,
  Preset,
  Product,
  ProductField,
  ProductType,
  Section,
  Space,
  SpaceMember,
  SpacePreview,
  User,
  Variant,
} from "../models";
import { type EntitiesMap } from "./autoNormalize";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Describes which nested properties should be extracted into entity buckets. */
export interface FlattenConfig {
  [key: string]: keyof EntitiesMap | FlattenConfig;
}

export type TypeRegistryEntry<T = AnyObject> = {
  /**
   * Transforms raw API data into the Redux-ready shape. Optional — when omitted
   * the walker applies its default deep camelCase + keyTransforms (equivalent
   * to the legacy `x => camelize(x)` behaviour) without any bespoke reshape.
   */
  formatter?: (data: unknown) => T;
  /** The key in `state.entities` where this entity lives (e.g. "kits"). Null means the entity stays inline and is not stored. */
  storeKey: keyof EntitiesMap;
  /** Extracts the entity's unique ID from the *formatted* object. */
  getId: (entity: T) => string;
  /** Which properties hold nested entities that should be flattened out. */
  flatten?: FlattenConfig;
};

/**
 * Binds a concrete entity type `T` to an entry so `getId`/`formatter` are
 * type-checked against the real model, then erases back to `AnyObject` so
 * the registry stays a uniform `Record<string, TypeRegistryEntry<AnyObject>>`.
 */
function defineEntry<T>(e: TypeRegistryEntry<T>): TypeRegistryEntry<AnyObject> {
  return e as unknown as TypeRegistryEntry<AnyObject>;
}

// ---------------------------------------------------------------------------
// Registry – keyed by the API's `__type` value
// ---------------------------------------------------------------------------

export const typeRegistry: Record<string, TypeRegistryEntry<AnyObject>> = {
  user: defineEntry<User>({
    storeKey: "users",
    getId: e => String(e.id),
    flatten: { spaces: "spaces" },
  }),
  space: defineEntry<Space>({
    formatter: formatSpace,
    storeKey: "spaces",
    getId: e => String(e.id),
    flatten: { fields: "fields", kits: "kits" },
  }),
  space_preview: defineEntry<SpacePreview>({
    formatter: formatSpacePreview,
    storeKey: null,
    getId: e => String(e.id),
  }),
  space_access: defineEntry<SpaceMember>({
    formatter: formatSpaceMember,
    storeKey: "spaceMembers",
    getId: e => `${e.spaceId}-${e.userId}`,
    flatten: { user: "users", kitAccess: "kitMembers", portalAccess: "portalMembers" },
  }),
  kit: defineEntry<Kit>({
    formatter: formatKit,
    storeKey: "kits",
    getId: e => String(e.kitUuid),
    flatten: { space: "spaces", versions: "kitVersions" },
  }),
  kit_access: defineEntry<KitMember>({
    formatter: formatKitMember as (data: unknown) => KitMember,
    storeKey: "kitMembers",
    getId: e => `${e.spaceId}-${e.userId}-${e.kitUuid}`,
    flatten: { kit: "kits", user: "users" },
  }),
  portal_access: defineEntry<PortalMember>({
    formatter: formatPortalMember,
    storeKey: "portalMembers",
    getId: e => `${e.portalUuid}-${e.userId}`,
    flatten: { portal: "portals", user: "users" },
  }),
  version: defineEntry<KitVersion>({
    formatter: formatKitVersion,
    storeKey: "kitVersions",
    getId: e => `${e.kitUuid}-${e.version}`,
    flatten: { sections: "sections", creator: "users" },
  }),
  section: defineEntry<Section>({
    formatter: formatSection,
    storeKey: "sections",
    getId: e => `${e.uuid}-${e.version}`,
    flatten: { items: "items" },
  }),
  item: defineEntry<Item>({
    formatter: formatItem,
    storeKey: "items",
    getId: e => `${e.uuid}-${e.version}`,
    flatten: {
      section: "sections",
      kit: "kits",
      kitVersion: "kitVersions",
      space: "spaces",
      asset: "assets",
      item: "items",
      items: "items",
    },
  }),
  asset: defineEntry<Asset>({
    formatter: formatAsset,
    storeKey: "assets",
    getId: e => String(e.uuid),
    flatten: { items: "items", uploader: "users" },
  }),
  outline_section: defineEntry<Section>({
    formatter: formatSection,
    storeKey: null,
    getId: e => String(e.uuid),
  }),
  invitation: defineEntry<Invitation>({
    storeKey: "invitations",
    getId: e => String(e.id),
  }),
  field: defineEntry<CustomField>({
    storeKey: "fields",
    getId: e => String(e.id),
    flatten: { space: "spaces" },
  }),
  portal: defineEntry<Portal>({
    formatter: formatPortal,
    storeKey: "portals",
    getId: e => String(e.uuid),
    flatten: { space: "spaces" },
  }),
  portal_item: defineEntry<PortalItem>({
    formatter: formatPortalItem,
    storeKey: "portalItems",
    getId: e => String(e.uuid),
    flatten: { kit: "kits", asset: "assets" },
  }),
  portal_member: defineEntry<PortalMember>({
    formatter: formatPortalMember,
    storeKey: "portalMembers",
    getId: e => `${e.portalUuid}-${e.userId}`,
    flatten: { portal: "portals", user: "users" },
  }),
  download_request: defineEntry<DownloadRequest>({
    storeKey: "downloadRequests",
    getId: e => String(e.id),
  }),
  view: defineEntry<AssetView>({
    storeKey: "views",
    getId: e => String(e.id),
  }),
  api_token: defineEntry<APIToken>({
    storeKey: "apiTokens",
    getId: e => String(e.token),
  }),
  direct_link: defineEntry<DirectLink>({
    storeKey: "directLinks",
    getId: e => String(e.id),
  }),
  product_type: defineEntry<ProductType>({
    storeKey: "productTypes",
    getId: e => String(e.id),
    flatten: {},
  }),
  product_type_field: defineEntry<ProductField>({
    formatter: formatField as (data: unknown) => ProductField,
    storeKey: "productFields",
    getId: e => String(e.id),
  }),
  market: defineEntry<Market>({
    storeKey: "markets",
    getId: e => String(e.id),
  }),
  product: defineEntry<Product>({
    formatter: formatProduct,
    storeKey: "products",
    getId: e => String(e.uuid),
    flatten: {
      productType: "productTypes",
      previewAsset: "assets",
      variants: { assets: "assets", fields: { assets: { asset: "assets" } } },
      fields: { assets: { asset: "assets" } },
    },
  }),
  variant: defineEntry<Variant>({
    formatter: formatVariant as (data: unknown) => Variant,
    storeKey: null,
    getId: e => String(e.id),
    flatten: {
      product: "products",
      assets: "assets",
      fields: { assets: { asset: "assets" } },
    },
  }),
  preset: defineEntry<Preset>({
    storeKey: "presets",
    getId: e => String(e.key),
  }),
};

// ---------------------------------------------------------------------------
// Derived lookup: storeKey → { getId, flatten }
//
// Used by the flattener when it encounters a nested formatted entity and
// needs to know how to extract its ID and recursively flatten its children.
// Entries with a null storeKey (e.g. space_preview, variant) stay inline
// and are never stored, so they're skipped here.
// ---------------------------------------------------------------------------

export const storeRegistry: Record<
  string,
  { getId: (e: AnyObject) => string; flatten: FlattenConfig }
> = {};

for (const entry of Object.values(typeRegistry)) {
  if (!entry.storeKey) continue;
  storeRegistry[entry.storeKey] = { getId: entry.getId, flatten: entry.flatten };
}
